import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Button,
  Badge,
  Skeleton,
} from "@asm/ui";
import {
  ArrowLeft,
  Radar,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Settings2,
  LayoutDashboard,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { scansApi, type Scan } from "@/lib/api";
import { simplifyResults } from "@/lib/simplify-results";
import {
  RiskOverviewCard,
  PriorityActionsCard,
  CategorySummaryCards,
  ExportMenu,
} from "@/components/simple-results";
import type { FindingType } from "@/lib/api";
import type { BusinessCategory } from "@/lib/simplify-results";

// Maps finding types to their source module for navigation
const FINDING_TYPE_TO_MODULE: Partial<Record<FindingType, string>> = {
  certificate_expired: "tls",
  certificate_expiring_soon: "tls",
  certificate_weak_signature: "tls",
  certificate_self_signed: "tls",
  certificate_hostname_mismatch: "tls",
  certificate_chain_incomplete: "tls",
  dns_missing_ns: "dns",
  dns_dangling_cname: "dns",
  dns_zone_transfer_enabled: "dns",
  dns_no_sec_dnssec: "dns",
  spf_missing: "email",
  spf_too_permissive: "email",
  dkim_missing: "email",
  dmarc_missing: "email",
  dmarc_none: "email",
  dmarc_quarantine: "email",
  headers_missing_hsts: "headers",
  headers_missing_csp: "headers",
  headers_missing_xfo: "headers",
  headers_missing_xcto: "headers",
  headers_server_info_leak: "headers",
  headers_powered_by_leak: "headers",
  tls_weak_cipher: "tls",
  tls_old_protocol: "tls",
  tls_missing_pfs: "tls",
  exposure_open_port: "portscan",
  exposure_sensitive_port: "portscan",
  exposure_known_vulnerability: "vuln",
  exposure_credential_leak: "repos",
  exposure_data_breach: "repos",
  cloud_public_bucket: "cloud",
  cloud_misconfigured_iam: "cloud_misconfig",
  cloud_open_security_group: "cloud_misconfig",
  repo_secrets_exposed: "repos",
  repo_sensitive_files: "repos",
  tech_outdated_software: "tech",
  tech_end_of_life: "tech",
};

// Category to module tab mapping for navigation
const CATEGORY_TO_MODULE: Record<BusinessCategory, string> = {
  email: "email",
  website: "tls",
  domain: "dns",
  exposure: "portscan",
  brand: "typosquat",
};

// Status badge component
function StatusBadge({ status }: { status: Scan["status"] }) {
  const statusConfig: Record<Scan["status"], { icon: typeof Clock; className: string; label: string }> = {
    pending: { icon: Clock, className: "border-amber-500/30 text-amber-400 bg-amber-500/10", label: "Pending" },
    running: { icon: Loader2, className: "border-cyan-500/30 text-cyan-400 bg-cyan-500/10", label: "Running" },
    completed: { icon: CheckCircle2, className: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10", label: "Completed" },
    failed: { icon: XCircle, className: "border-red-500/30 text-red-400 bg-red-500/10", label: "Failed" },
    cancelled: { icon: AlertTriangle, className: "border-muted text-muted-foreground bg-muted/50", label: "Cancelled" },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={`${config.className} px-3 py-1.5`}>
      <Icon className={`h-3.5 w-3.5 mr-1.5 ${status === "running" ? "animate-spin" : ""}`} />
      {config.label}
    </Badge>
  );
}

// View toggle component
function ViewToggle({ currentView }: { currentView: "simple" | "advanced" }) {
  const { id } = useParams<{ id: string }>();

  return (
    <div className="inline-flex rounded-lg border border-border bg-muted/30 p-1">
      <Link to={`/scans/${id}`}>
        <Button
          variant={currentView === "simple" ? "default" : "ghost"}
          size="sm"
          className={`gap-2 rounded-md ${currentView === "simple" ? "bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" : "hover:bg-muted"}`}
        >
          <LayoutDashboard className="h-4 w-4" />
          Simple
        </Button>
      </Link>
      <Link to={`/scans/${id}/advanced`}>
        <Button
          variant={currentView === "advanced" ? "default" : "ghost"}
          size="sm"
          className={`gap-2 rounded-md ${currentView === "advanced" ? "bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" : "hover:bg-muted"}`}
        >
          <Settings2 className="h-4 w-4" />
          Advanced
        </Button>
      </Link>
    </div>
  );
}

// Progress indicator for running scans
function ProgressIndicator({ progress }: { progress: NonNullable<Scan["progress"]> }) {
  return (
    <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-cyan-500/20">
          <Loader2 className="h-5 w-5 text-cyan-400 animate-spin" />
        </div>
        <div>
          <h3 className="font-semibold text-cyan-50">Scan in Progress</h3>
          <p className="text-sm text-cyan-400/80">Analyzing your security posture...</p>
        </div>
      </div>
      <div className="relative h-3 bg-muted rounded-full overflow-hidden mb-2">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full transition-all duration-500"
          style={{ width: `${progress.percent}%` }}
        />
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">
          {progress.modules_completed} of {progress.modules_total} checks completed
        </span>
        <span className="font-mono text-cyan-400 font-semibold">{progress.percent}%</span>
      </div>
    </div>
  );
}

export default function ScanDetailsSimple() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showAllActions, setShowAllActions] = useState(false);

  const { data: scan, isLoading: scanLoading } = useQuery({
    queryKey: ["scan", id],
    queryFn: () => scansApi.get(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const scan = query.state.data;
      if (scan?.status === "running" || scan?.status === "pending") return 1000;
      return false;
    },
  });

  const { data: results, isLoading: resultsLoading } = useQuery({
    queryKey: ["scanResults", id],
    queryFn: () => scansApi.getResults(id!),
    enabled: !!id && (scan?.status === "completed" || scan?.status === "failed"),
  });

  const simplifiedResults = results ? simplifyResults(results) : null;

  if (scanLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-16 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">Scan not found</h3>
        <p className="text-muted-foreground text-sm mb-6">The scan you're looking for doesn't exist or has been deleted.</p>
        <Button asChild className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold">
          <Link to="/scans"><ArrowLeft className="mr-2 h-4 w-4" />Back to Scans</Link>
        </Button>
      </div>
    );
  }

  const topActions = simplifiedResults?.priorityActions.slice(0, 3) || [];
  const remainingActions = simplifiedResults?.priorityActions.slice(3) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate("/scans")}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Scans
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <Radar className="h-6 w-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight font-mono">{scan.target}</h1>
              <p className="text-muted-foreground text-sm">
                Scanned {new Date(scan.created_at).toLocaleDateString(undefined, {
                  month: "long", day: "numeric", year: "numeric",
                })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ViewToggle currentView="simple" />
            <StatusBadge status={scan.status} />
          </div>
        </div>
      </div>

      {/* Progress indicator for running scans */}
      {scan.status === "running" && scan.progress && (
        <ProgressIndicator progress={scan.progress} />
      )}

      {/* Error display */}
      {scan.status === "failed" && scan.error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div>
              <h3 className="font-semibold text-red-50">Scan Encountered Issues</h3>
              <p className="text-sm text-red-400/80">Some checks couldn't be completed, but partial results are available.</p>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {(scan.status === "completed" || scan.status === "failed") && (
        <>
          {resultsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-24 rounded-xl" />
              <Skeleton className="h-48 rounded-xl" />
            </div>
          ) : simplifiedResults ? (
            <>
              {/* Compact risk summary — single row */}
              <RiskOverviewCard
                riskLevel={simplifiedResults.score.riskLevel}
                assessment={simplifiedResults.score.assessment}
                findingCount={simplifiedResults.totalFindings}
                distribution={simplifiedResults.distribution}
              />

              {/* What to Do Now — top 3 actions prominently */}
              {topActions.length > 0 && (
                <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
                  <div className="p-5 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-6 w-1 rounded-full bg-red-500" />
                      <div>
                        <h3 className="font-semibold text-lg">What to Do Now</h3>
                        <p className="text-sm text-muted-foreground">
                          {simplifiedResults.urgentCount > 0 ? (
                            <><span className="text-red-400 font-medium">{simplifiedResults.urgentCount} urgent</span>{" "}— address these first</>
                          ) : (
                            `${topActions.length} items to review`
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="px-5 pb-5">
                    <PriorityActionsCard
                      actions={showAllActions ? simplifiedResults.priorityActions : topActions}
                      onActionClick={(action) => {
                        const module = FINDING_TYPE_TO_MODULE[action.findingType] || "insights";
                        navigate(`/scans/${id}/advanced?tab=${module}`);
                      }}
                    />
                    {remainingActions.length > 0 && (
                      <button
                        onClick={() => setShowAllActions(!showAllActions)}
                        className="flex items-center gap-2 mx-auto mt-3 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                      >
                        {showAllActions ? (
                          <>Show less <ChevronUp className="h-4 w-4" /></>
                        ) : (
                          <>Show {remainingActions.length} more items <ChevronDown className="h-4 w-4" /></>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Category health overview — interactive */}
              {simplifiedResults.categories.length > 0 && (
                <CategorySummaryCards
                  categories={simplifiedResults.categories}
                  onCategoryClick={(category) => {
                    const module = CATEGORY_TO_MODULE[category.category] || "insights";
                    navigate(`/scans/${id}/advanced?tab=${module}`);
                  }}
                />
              )}

              {/* Footer with export + advanced view link */}
              <div className="rounded-xl border border-border bg-card/30 p-5">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="font-medium mb-1">Dive deeper into technical findings</h3>
                    <p className="text-sm text-muted-foreground">
                      View raw data, per-module details, and export options.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <ExportMenu
                      results={simplifiedResults}
                      metadata={{
                        target: scan.target,
                        scannedAt: scan.created_at,
                        exportedAt: new Date().toISOString(),
                        scanId: scan.id,
                      }}
                    />
                    <Button
                      asChild variant="outline" size="sm"
                      className="gap-2 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                    >
                      <Link to={`/scans/${id}/advanced`}>
                        <Settings2 className="h-4 w-4" />
                        View Advanced Details
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 rounded-xl border border-border bg-card/30">
              <p className="text-muted-foreground">Results are being processed. Please check back shortly.</p>
            </div>
          )}
        </>
      )}

      {/* Pending state */}
      {scan.status === "pending" && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-8 text-center">
          <Clock className="h-8 w-8 text-amber-400 mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">Scan Queued</h3>
          <p className="text-muted-foreground">Your security scan is queued and will start shortly.</p>
        </div>
      )}
    </div>
  );
}
